/** Anything a log field may hold; objects are serialized to bounded JSON. */
export type LogValue = Error | string | number | boolean | bigint | object | null | undefined;

/** Whatever was thrown: a `catch` variable is `unknown`, so callers can pass it straight through. */
export type ThrownValue = unknown;

export type ErrorLevel = 'fatal' | 'error' | 'warning' | 'info' | 'debug';

export type ErrorContext = {
  tags?: Record<string, string | number | boolean>;
  extra?: Record<string, LogValue>;
  level?: ErrorLevel;
  fingerprint?: string[];
  contexts?: Record<string, LogValue>;
};

/**
 * The error-reporting surface the apps and `@trinity/chat-components` (`ChatLoggerProvider`) already use.
 * It deliberately mirrors the shape Sentry exposes, so a Sentry-backed and an OpenTelemetry-backed logger are
 * interchangeable and can be composed.
 */
export type TelemetryLogger = {
  addBreadcrumb: (breadcrumb: { message?: string }) => void;
  captureMessage: (message: string) => void;
  captureException: (error: ThrownValue, context?: ErrorContext) => void;
  setContext: (name: string, context: Record<string, LogValue>) => void;
};

/** Runs every call on each logger, so one failing sink never stops the others. */
export function composeLoggers(...loggers: TelemetryLogger[]): TelemetryLogger {
  const each = (call: (logger: TelemetryLogger) => void) => {
    for (const logger of loggers) {
      try {
        call(logger);
      } catch {
        // A reporting failure must never break the app, or starve the remaining sinks.
      }
    }
  };

  return {
    addBreadcrumb: (breadcrumb) => each((logger) => logger.addBreadcrumb(breadcrumb)),
    captureMessage: (message) => each((logger) => logger.captureMessage(message)),
    captureException: (error, context) => each((logger) => logger.captureException(error, context)),
    setContext: (name, context) => each((logger) => logger.setContext(name, context)),
  };
}

export const noopLogger: TelemetryLogger = {
  addBreadcrumb: () => undefined,
  captureMessage: () => undefined,
  captureException: () => undefined,
  setContext: () => undefined,
};
